import { demoFunc } from './dees-simple-login.demo.js';

import {
  customElement,
  html,
  DeesElement,
  property,
  type TemplateResult,
  cssManager,
  css,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-layout/dees-tile/dees-tile.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-simple-login': DeesSimpleLogin;
  }
}

@customElement('dees-simple-login')
export class DeesSimpleLogin extends DeesElement {
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['Simple'];
  // INSTANCE

  @property()
  accessor name: string = 'Application';

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        color: var(--dees-color-text-primary);
        user-select: none;
        display: block;
        width: 100%;
        height: 100%;
        font-family: var(--dees-font-family);
      }

      .loginContainer {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: var(--dees-color-bg-primary);
      }

      .slotContainer {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        opacity: 0;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), transform var(--dees-transition-default) var(--dees-ease-standard);
        pointer-events: none;
      }

      .login {
        width: 100%;
        max-width: 360px;
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-xl);
      }

      .login-header {
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-sm);
        text-align: center;
      }

      .header {
        font-size: var(--dees-font-title2-size);
        font-weight: 600;
        letter-spacing: -0.025em;
        color: var(--dees-color-text-primary);
      }

      .subheader {
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-muted);
      }

      dees-tile {
        width: 100%;
      }

      dees-tile::part(content) {
        padding: var(--dees-spacing-xl);
      }

      dees-tile dees-form {
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-lg);
      }

      dees-tile dees-input-text {
        width: 100%;
      }

      dees-tile dees-form-submit {
        margin-top: var(--dees-spacing-sm);
        width: 100%;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="loginContainer">
        <div class="login">
          <div class="login-header">
            <div class="header">Sign in</div>
            <div class="subheader">Enter your credentials to access ${this.name}</div>
          </div>
          <dees-tile .heading=${'Credentials'}>
            <dees-form>
              <dees-input-text key="username" label="Username" required></dees-input-text>
              <dees-input-text key="password" label="Password" isPasswordBool required></dees-input-text>
              <dees-form-submit>Sign in</dees-form-submit>
            </dees-form>
          </dees-tile>
        </div>
      </div>
      <div class="slotContainer">
        <slot></slot>
      </div>
    `;
  }

  public async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.firstUpdated(_changedProperties);
    
    const form = this.shadowRoot!.querySelector('dees-form') as any;
    if (form) {
      form.addEventListener('formData', ((event: CustomEvent) => {
        this.dispatchEvent(new CustomEvent('login', { 
          detail: event.detail, 
          bubbles: true, 
          composed: true 
        }));
      }) as EventListener);
    }
  }

  /**
   * allows switching to slotted content
   */
  public async switchToSlottedContent() {
    const domtools = await this.domtoolsPromise;
    const loginDiv = this.shadowRoot!.querySelector('.login') as HTMLDivElement;
    const loginContainerDiv = this.shadowRoot!.querySelector('.loginContainer') as HTMLDivElement;
    const slotContainerDiv = this.shadowRoot!.querySelector('.slotContainer') as HTMLDivElement;
    loginDiv.style.opacity = '0';
    loginDiv.style.transform = 'translateY(20px)';
    loginContainerDiv.style.pointerEvents = 'none';
    slotContainerDiv.style.transform = 'translateY(20px)';
    await domtools.convenience.smartdelay.delayFor(300);
    slotContainerDiv.style.opacity = '1';
    slotContainerDiv.style.transform = 'translateY(0px)';
    await domtools.convenience.smartdelay.delayFor(300);
    slotContainerDiv.style.pointerEvents = 'all';
  }
}
